import React, { useState, useEffect } from 'react';
import { getParrainages, getProspects } from '../lib/api';
import { Link } from 'react-router-dom';
import { 
    Gift, 
    Users, 
    TrendingUp,
    ChevronRight,
    UserCheck,
    UserX
} from 'lucide-react';
import { toast } from 'sonner';

export const ParrainagesPage = () => {
    const [parrainages, setParrainages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const res = await getParrainages();
            setParrainages(res.data);
        } catch (error) {
            console.error('Error fetching parrainages:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    // Stats
    const totalParrains = parrainages.length;
    const totalFilleuls = parrainages.reduce((acc, p) => acc + p.total_filleuls, 0);
    const filleulsConvertis = parrainages.reduce((acc, p) => acc + p.filleuls_convertis, 0);
    const tauxConversion = totalFilleuls > 0 ? Math.round((filleulsConvertis / totalFilleuls) * 100) : 0;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="parrainages-page">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-[#0A0A0A] tracking-tight">Parrainages</h1>
                <p className="text-[#52525B] mt-1">Suivez les recommandations et parrainages clients</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-[#E85A24]">
                            <Gift className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="field-label">Parrains actifs</p>
                            <p className="text-2xl font-bold">{totalParrains}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-[#0284C7]">
                            <Users className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="field-label">Filleuls total</p>
                            <p className="text-2xl font-bold">{totalFilleuls}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-[#16A34A]">
                            <UserCheck className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="field-label">Filleuls convertis</p>
                            <p className="text-2xl font-bold">{filleulsConvertis}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-[#E4E4E7] p-6">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-[#7C3AED]">
                            <TrendingUp className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="field-label">Taux conversion</p>
                            <p className="text-2xl font-bold">{tauxConversion}%</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Parrains List */}
            <div className="bg-white border border-[#E4E4E7]">
                <div className="p-4 border-b border-[#E4E4E7]">
                    <h2 className="font-semibold text-[#0A0A0A]">Liste des parrains</h2>
                </div>
                
                {parrainages.length === 0 ? (
                    <div className="text-center py-12 text-[#52525B]">
                        <Gift className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Aucun parrainage enregistré</p>
                        <p className="text-sm mt-1">Les parrainages apparaîtront lorsque vous ajouterez des prospects avec un parrain</p>
                    </div>
                ) : (
                    <div className="divide-y divide-[#E4E4E7]">
                        {parrainages.map(parrainage => (
                            <div key={parrainage.parrain_id} className="p-4" data-testid={`parrain-${parrainage.parrain_id}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <Link 
                                        to={`/prospects/${parrainage.parrain_id}`}
                                        className="flex items-center gap-3 hover:text-[#E85A24] transition-colors"
                                    >
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${parrainage.parrain_is_client ? 'bg-[#16A34A]' : 'bg-[#E85A24]'}`}>
                                            {parrainage.parrain_nom?.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-medium">{parrainage.parrain_nom}</p>
                                            <p className="text-sm text-[#52525B]">
                                                {parrainage.parrain_is_client ? 'Client' : 'Prospect'}
                                            </p>
                                        </div>
                                    </Link>
                                    <div className="text-right">
                                        <p className="font-semibold text-[#E85A24]">{parrainage.total_filleuls} filleul(s)</p>
                                        <p className="text-sm text-[#52525B]">{parrainage.filleuls_convertis} converti(s)</p>
                                    </div>
                                </div>
                                
                                {/* Filleuls */}
                                <div className="ml-13 space-y-2">
                                    {parrainage.filleuls.map(filleul => (
                                        <Link 
                                            key={filleul.id}
                                            to={`/prospects/${filleul.id}`}
                                            className="flex items-center justify-between p-2 bg-[#FAFAFA] hover:bg-[#F4F4F5] transition-colors rounded-sm"
                                        >
                                            <div className="flex items-center gap-2">
                                                <ChevronRight className="w-4 h-4 text-[#52525B]" />
                                                <span className="text-sm">{filleul.nom}</span>
                                            </div>
                                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-sm ${
                                                filleul.is_client 
                                                    ? 'bg-[#16A34A]/10 text-[#16A34A]' 
                                                    : 'bg-[#52525B]/10 text-[#52525B]'
                                            }`}>
                                                {filleul.is_client ? 'Client' : 'Prospect'}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
